import { Clickable, Col, Icon, Row, Text } from 'components';
import { theme } from 'lib';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

// Quiz state values come from the backend `resolveQuizState` (lib/quizState.ts).
// We mirror the four enum strings without importing the TS enum to keep this isomorphic.
export const QUIZ_STATE = {
  READY: 'ready_to_play',
  COMPLETED: 'completed',
  COMING_UP: 'coming_up',
  LOCKED: 'locked'
};

// Match Shoutout card width so the three rows on the profile page (Shoutouts /
// Quick Quiz / Latest Posts) align edge-to-edge. 3 × 296 + 2 × 24 = 936 exactly
// — the same width as the profile's content column.
const CARD_WIDTH_DESKTOP = 296;
const CARD_WIDTH_MOBILE = 240;

// Background + spacing mirror the Shoutout card (theme.GREY_10, no border)
// so the two profile rows share the same visual language. Hover lifts the
// card with a soft shadow — same affordance Shoutout uses.
const Card = styled.div`
  width: ${(p) => (p.isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP)}px;
  min-width: ${(p) => (p.isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP)}px;
  background: ${theme.GREY_10};
  border-radius: 12px;
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 160px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${(p) => p.bg};
  color: ${(p) => p.col};
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(39, 227, 163, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(39, 227, 163, 0); }
  100% { box-shadow: 0 0 0 0 rgba(39, 227, 163, 0); }
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.GREEN};
  animation: ${pulse} 1.6s infinite;
`;

const Question = styled.p`
  font-family: CeraPro;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  color: ${theme.GREY_90};
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Pill / action / icon-hover use white now that the card background is
// GREY_10 — keeps secondary chrome readable against the new card tone.
// `gap` leaves room for the reward star icon prefix.
const PointsPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  color: ${theme.GREY_70};
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
`;

const ActionBtn = styled.button`
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) =>
    p.tone === 'primary' ? theme.SECONDARY : p.tone === 'muted' ? '#fff' : '#fff'};
  color: ${(p) =>
    p.tone === 'primary' ? '#fff' : p.tone === 'muted' ? theme.GREY_60 : theme.GREY_80};
  border: 1px solid
    ${(p) => (p.tone === 'primary' ? theme.SECONDARY : p.tone === 'muted' ? theme.GREY_20 : theme.GREY_20)};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  color: ${(p) => (p.danger ? theme.SECONDARY : theme.GREY_60)};
  &:hover {
    background: #fff;
  }
`;

// Translate the backend ButtonState into a compact visual label for the chip.
// Returns null when the chip should be hidden (e.g. fan looking at a "ready" quiz —
// the LIVE badge takes priority and is rendered separately).
const stateChip = (state, t) => {
  if (state === QUIZ_STATE.COMPLETED) {
    return { label: t('pollQuizProfile.chipAnswered'), bg: `${theme.GREEN}20`, col: theme.GREEN };
  }
  if (state === QUIZ_STATE.COMING_UP) {
    return { label: t('pollQuizProfile.chipComingUp'), bg: `${theme.PURPLE}20`, col: theme.PURPLE };
  }
  if (state === QUIZ_STATE.LOCKED) {
    return { label: t('pollQuizProfile.chipClosed'), bg: theme.GREY_10, col: theme.GREY_60 };
  }
  return null;
};

const QuizCard = ({
  quiz,
  isMobile,
  canManage,
  onPlay,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const { t } = useTranslation();
  const isLive = quiz.state === QUIZ_STATE.READY && quiz.isActive !== false;
  const isAnswered = quiz.answered === true || quiz.state === QUIZ_STATE.COMPLETED;
  const chip = stateChip(quiz.state, t);

  // Pick the action label visible to the fan. The whole card is clickable too;
  // the button is just a strong affordance.
  let actionLabel = t('pollQuizProfile.actionPlay');
  let actionDisabled = false;
  if (isAnswered) {
    actionLabel = t('pollQuizProfile.actionViewResult');
  } else if (quiz.state === QUIZ_STATE.COMING_UP) {
    actionLabel = t('pollQuizProfile.chipComingUp');
    actionDisabled = true;
  } else if (quiz.state === QUIZ_STATE.LOCKED) {
    actionLabel = t('pollQuizProfile.chipClosed');
    actionDisabled = true;
  }

  const handleCardClick = () => {
    if (actionDisabled && !canManage) return;
    if (canManage) {
      // Admin tap on the card → edit. Fan-like actions (delete/toggle) are exposed
      // as small icon buttons so the primary tap remains a quick edit.
      onEdit?.(quiz);
    } else {
      onPlay?.(quiz);
    }
  };

  return (
    <Card isMobile={isMobile} onClick={handleCardClick}>
      <Col noFlex>
        <Row between center>
          {isLive ? (
            <Chip bg={`${theme.GREEN}20`} col={theme.GREEN}>
              <LiveDot /> {t('pollQuizProfile.live')}
            </Chip>
          ) : chip ? (
            <Chip bg={chip.bg} col={chip.col}>
              {chip.label}
            </Chip>
          ) : (
            <span />
          )}
          {/* Inactive ribbon — admin-only visual cue for quizzes the team has paused. */}
          {canManage && quiz.isActive === false && (
            <Chip bg={theme.GREY_10} col={theme.GREY_60}>
              {t('pollQuizProfile.inactive')}
            </Chip>
          )}
        </Row>

        <Question>{quiz.question}</Question>
      </Col>

      <Row between center>
        <PointsPill>
          {/* Unicode star — the bundled SVG `starFilled` lacks a viewBox and
              clips below 20px (renders as a pink blob). The glyph scales
              cleanly with font-size and reads as "reward" in any context. */}
          <span style={{ color: theme.SECONDARY, fontSize: 12, lineHeight: 1 }}>★</span>
          {t('pollQuizProfile.pts', { count: quiz.rewardPoints || 0 })}
        </PointsPill>

        {canManage ? (
          // Admin row: small icon buttons that don't trigger the card-level edit.
          // stopPropagation keeps tap-on-icon from also opening the edit form.
          <Row noFlex center gap="4px">
            <IconBtn
              type="button"
              title={quiz.isActive ? t('pollQuizProfile.deactivate') : t('pollQuizProfile.activate')}
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive?.(quiz);
              }}
            >
              {/* `pause` while live, `play` while paused — same logic as a media toggle. */}
              <Icon type={quiz.isActive ? 'pause' : 'play'} dimensions={14} />
            </IconBtn>
            <IconBtn
              type="button"
              danger
              title={t('pollQuizProfile.delete')}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(quiz);
              }}
            >
              <Icon type="delete" dimensions={14} col="currentColor" />
            </IconBtn>
            <Clickable
              onClick={(e) => {
                e?.stopPropagation();
                onEdit?.(quiz);
              }}
            >
              <Text c2 bold col={theme.SECONDARY}>
                {t('pollQuizProfile.edit')}
              </Text>
            </Clickable>
          </Row>
        ) : (
          <ActionBtn
            tone={isAnswered ? 'muted' : actionDisabled ? 'default' : 'primary'}
            disabled={actionDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!actionDisabled) onPlay?.(quiz);
            }}
          >
            {actionLabel}
          </ActionBtn>
        )}
      </Row>
    </Card>
  );
};

// Memoize — the row re-renders on every fetch/optimistic-update but most cards
// don't change. React.memo with default shallow compare is sufficient because
// the quiz object reference is replaced (not mutated) on updates.
export default memo(QuizCard);
